import { useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { OrbitingPair } from "../components/OrbitingPair";
import { RichText } from "../components/RichText";
import { submitQuote } from "../lib/api";
import { toast } from "sonner";
import { useT } from "../lib/LanguageContext";

const HERO_IMG =
  "https://images.pexels.com/photos/14079666/pexels-photo-14079666.jpeg";
const WOMAN_SKYLINE =
  "https://customer-assets.emergentagent.com/job_digital-monaco/artifacts/8bqvjd5y_ChatGPT%20Image%20Mar%2017%2C%202026%2C%2012_00_14%20PM.png";
const LAPTOP_IMG =
  "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?auto=format&fit=crop&w=1200&q=80";
const MONACO_NIGHT =
  "https://customer-assets.emergentagent.com/job_digital-monaco/artifacts/ukgf4yca_13.jpg";

const TRUST_LOGOS = [
  {
    name: "The Queen Bee",
    src: "https://customer-assets.emergentagent.com/job_digital-monaco/artifacts/mfhaimlp_Queen_Bee_Logo_Black.webp",
    className: "max-h-24 md:max-h-28",
  },
  {
    name: "Sellier Monaco",
    src: "https://customer-assets.emergentagent.com/job_digital-monaco/artifacts/ofbcct0q_sellier.jpg",
    className: "max-h-24 md:max-h-32 max-w-[220px] md:max-w-[280px]",
  },
  {
    name: "Visit Monaco",
    src: "https://customer-assets.emergentagent.com/job_digital-monaco/artifacts/9unowrrx_visit-monaco-logo.jpeg",
    className: "max-h-12 md:max-h-16 max-w-[160px] md:max-w-[200px]",
  },
];

const TIER_SLUGS = ["focus", "start", "essential", "premium"];

const Accueil = () => {
  const { t } = useT();
  const [form, setForm] = useState({ prenom: "", nom: "", email: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitQuote(form);
      toast.success(t("common.successDevis"));
      setForm({ prenom: "", nom: "", email: "" });
    } catch {
      toast.error(t("common.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  const steps = t("home.steps");
  const tiers = t("home.tiers");

  return (
    <main data-testid="page-accueil">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Monaco"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 px-6 max-w-5xl text-center text-white">
          <Reveal>
            <h1 className="font-serif font-bold uppercase tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
              {t("home.heroTitle1")}{" "}
              <span className="italic font-light">{t("home.heroTitleItalic")}</span>{" "}
              {t("home.heroTitle2")}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg md:text-2xl font-light italic text-white">
              {t("home.heroTagline")}
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
              <Link to="/offres" data-testid="hero-cta-offres" className="btn-violet">
                {t("home.heroCta")}
              </Link>
              <Link to="/contact#rdv" data-testid="hero-cta-rdv" className="btn-yellow">
                {t("home.heroCta2")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-white py-28 md:py-40 px-6">
        <div className="max-w-3xl mx-auto text-center text-[#6B5CE7] space-y-7">
          <Reveal>
            <p className="font-serif text-2xl md:text-3xl italic leading-relaxed">{t("home.intro1")}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base md:text-lg font-light leading-relaxed text-[#4A4558]"><RichText text={t("home.intro2")} /></p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="text-base md:text-lg font-light leading-relaxed text-[#4A4558]"><RichText text={t("home.intro3")} /></p>
          </Reveal>
        </div>
      </section>

      {/* WOMAN SKYLINE */}
      <section className="relative h-[70vh] md:h-[85vh]">
        <img
          src={WOMAN_SKYLINE}
          alt={t("home.signaturePhrase")}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-12 md:bottom-20 left-0 right-0 text-center px-6">
          <Reveal>
            <p className="font-serif italic text-white text-3xl md:text-5xl leading-tight">
              {t("home.signaturePhrase")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* NOTRE APPROCHE */}
      <section className="bg-[#FFFBEA] py-28 md:py-40 px-6 overflow-x-clip">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl uppercase text-[#6B5CE7]">
              {t("home.approcheTitle")}
            </h2>
            <p className="font-serif italic text-[#6B5CE7] text-xl md:text-2xl mt-3">
              {t("home.approcheSub")}
            </p>
          </Reveal>

          <OrbitingPair
            images={[
              { src: LAPTOP_IMG },
              { src: MONACO_NIGHT, offset: true },
            ]}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14 mt-24">
            {steps.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.08}>
                <div data-testid={`step-${i + 1}`}>
                  <span className="font-serif text-5xl md:text-6xl text-[#6B5CE7]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-serif text-2xl text-[#1A1528] mt-3 mb-3 font-semibold">{s.t}</h3>
                  <p className="text-[#4A4558] font-light leading-relaxed"><RichText text={s.d} /></p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OFFRES SELECTOR */}
      <section className="bg-white py-28 md:py-40 px-6">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl uppercase font-bold text-[#6B5CE7] max-w-4xl">
              {t("home.offresTitle")}
            </h2>
            <p className="font-serif italic text-[#6B5CE7] text-lg md:text-xl mt-4">
              {t("home.offresSub")}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {tiers.map((tier, i) => (
              <Reveal key={TIER_SLUGS[i]} delay={i * 0.08}>
                <Link
                  to={`/offres/reseaux-sociaux#${TIER_SLUGS[i]}`}
                  data-testid={`tier-card-${TIER_SLUGS[i]}`}
                  className="block h-full border-2 border-[#F5E6A3] rounded-2xl p-7 text-[#6B5CE7] bg-white hover:bg-[#FFFBEA] hover:-translate-y-1 transition-all duration-300"
                >
                  <h3 data-testid={`tier-title-${TIER_SLUGS[i]}`} className="font-serif text-xl font-semibold mb-4 leading-snug text-[#1A1528]">{tier.title}</h3>
                  <p className="text-[#4A4558] font-light leading-relaxed text-sm mb-4">{tier.desc}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#6B5CE7] font-medium">{tier.note}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-white pt-4 pb-24 md:pt-8 md:pb-32 px-6" data-testid="trust-section">
        <div className="max-w-6xl mx-auto text-center">
          <Reveal>
            <h2 className="font-serif italic text-2xl md:text-3xl text-[#6B5CE7] mb-14">
              {t("home.trustTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-wrap items-center justify-evenly gap-10 md:gap-6">
              {TRUST_LOGOS.map((logo) => (
                <img
                  key={logo.name}
                  src={logo.src}
                  alt={logo.name}
                  data-testid={`trust-logo-${logo.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`w-auto object-contain ${logo.className}`}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section id="reserver" className="bg-white py-28 md:py-36 px-6 scroll-mt-24">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl uppercase font-bold text-[#6B5CE7]">
              {t("home.devisTitle")}
            </h2>
            <p className="text-[#6B5CE7] mt-4 font-light text-lg">{t("home.devisSub")}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <form
              onSubmit={onSubmit}
              data-testid="devis-form"
              className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 text-left"
            >
              <input
                required
                name="prenom"
                value={form.prenom}
                onChange={onChange}
                placeholder={t("home.devisPrenom")}
                data-testid="devis-prenom"
                className="px-5 py-4 rounded-full border-2 border-[#F5E6A3] focus:border-[#6B5CE7] outline-none transition-colors bg-white text-[#1A1528]"
              />
              <input
                required
                name="nom"
                value={form.nom}
                onChange={onChange}
                placeholder={t("home.devisNom")}
                data-testid="devis-nom"
                className="px-5 py-4 rounded-full border-2 border-[#F5E6A3] focus:border-[#6B5CE7] outline-none transition-colors bg-white text-[#1A1528]"
              />
              <input
                required
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder={t("home.devisEmail")}
                data-testid="devis-email"
                className="px-5 py-4 rounded-full border-2 border-[#F5E6A3] focus:border-[#6B5CE7] outline-none transition-colors bg-white text-[#1A1528]"
              />
              <div className="md:col-span-3 flex justify-center mt-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="devis-submit"
                  className="btn-violet disabled:opacity-60"
                >
                  {loading ? t("common.sending") : t("common.envoyer")}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Accueil;
